// One environment per stage: account + domain (+ prod flag). Adding an env is one
// line in STAGES; everything downstream is typed Stage / Service.
export interface StageConfig {
  /** AWS account this stage deploys to (an account id is not a secret). */
  account: string;
  /** Route53 apex the stage's hostnames live under, e.g. supply25.dev. */
  domain: string;
  /** The production stage: hyphen-less domains, approval gate, tracing. */
  prod?: boolean;
}

const DEV_ACCOUNT = "376903139206";
const PROD_ACCOUNT = "533267087043";

export const STAGES = {
  dev: { account: DEV_ACCOUNT, domain: "supply25.dev" },
  // wong shares the prod account — its supply25.com zone lives there.
  // Only dev is the separate account.
  wong: { account: PROD_ACCOUNT, domain: "supply25.com" },
  api: { account: PROD_ACCOUNT, domain: "supply25.com", prod: true },
} as const satisfies Record<string, StageConfig>;

/**
 * The AWS accounts. Each has one account layer in `packages/infra`, deployed
 * by the stage named in `stackPrefix`, which is also the prefix its stacks
 * were created with.
 */
export const ACCOUNTS = {
  dev: { id: DEV_ACCOUNT, stackPrefix: "dev" },
  prod: { id: PROD_ACCOUNT, stackPrefix: "api" },
} as const satisfies Record<
  string,
  { id: string; stackPrefix: keyof typeof STAGES }
>;

/** An ephemeral PR preview stage, e.g. `pr-123` (on-demand, dev account/zone). */
export type PrStage = `pr-${number}`;

/** A deploy stage: a fixed environment (a key of STAGES) or an on-demand PR preview. */
export type Stage = keyof typeof STAGES | PrStage;

/** Subdomain prefixes used to build hostnames (`<service>-<stage>.<domain>`). */
export type Service =
  | "app"
  | "assessment-app"
  | "poc-assessment-app"
  | "feedback-app"
  | "user"
  | "user-svc"
  | "assessment"
  | "tender-package"
  | "insights"
  | "auth"
  | "feedback"
  | "insights-app";

const PR_STAGE = /^pr-\d+$/;

export const isPrStage = (value: string | undefined): value is PrStage =>
  value !== undefined && PR_STAGE.test(value);

const isStage = (value: string | undefined): value is Stage =>
  (value !== undefined && value in STAGES) || isPrStage(value);

/** Validate + narrow an env string to a Stage; throws on anything unknown. */
export const asStage = (value: string | undefined): Stage => {
  if (isStage(value)) return value;
  throw new Error(
    `Unknown stage "${value}". Known stages: ${Object.keys(STAGES).join(", ")}, or pr-<number>.`,
  );
};

// PR previews are ephemeral and always deploy to the dev account / dev zone,
// reusing dev's wildcard cert + hosted zone (never standing up their own infra).
const PR_STAGE_CONFIG: StageConfig = {
  account: DEV_ACCOUNT,
  domain: "supply25.dev",
  prod: false,
};

export const stageConfig = (stage: Stage): StageConfig =>
  isPrStage(stage) ? PR_STAGE_CONFIG : STAGES[stage];

// Uniform across the repo (CloudFront certs must be us-east-1), so not per-stage.
export const PRIMARY_REGION = "eu-west-2";
export const CLOUDFRONT_CERT_REGION = "us-east-1";
/** Where cross-region S3 replicas live — DR redundancy outside PRIMARY_REGION. */
export const REPLICA_REGION = "eu-west-1";

// Host layer — bare hostnames for CDK constructs (custom domains, certs, DNS
// records, cookie scopes). Runtime code wants stageUrls below.
export const stageDomain = (stage: Stage): string => stageConfig(stage).domain;

export const serviceHost = (service: Service, stage: Stage): string =>
  `${service}-${stage}.${stageDomain(stage)}`;

const serviceOrigin = (service: Service, stage: Stage): string =>
  `https://${serviceHost(service, stage)}`;

/**
 * Runtime URLs (+ transactional sender) for a stage. Prod's frontends drop the
 * stage and rename (app.…, assessment.…); everything else is
 * `<service>-<stage>.<domain>`.
 */
export interface StageUrls {
  app: string;
  /** Frontegg auth origin — the prod tenant or the shared uat tenant. */
  auth: string;
  assessmentApp: string;
  /** The staff SQL workbench, insights.<domain> on prod. */
  insightsApp: string;
  newAssessmentApp: string;
  feedbackApp: string;
  /** user-service public API (login/oauth/session — what the app calls). */
  user: string;
  /** user-service service-to-service API (what the other backends call). */
  userSvc: string;
  assessment: string;
  tenderPackage: string;
  /** Verified SES sender identity (hello@<domain>). */
  sender: string;
  feedback: string;
}

export const stageUrls = (stage: Stage): StageUrls => {
  const { prod } = stageConfig(stage);
  return {
    auth: prod ? "https://auth.supply25.com" : "https://auth-uat.supply25.com",
    app: prod
      ? `https://app.${stageDomain(stage)}`
      : serviceOrigin("app", stage),
    assessmentApp: prod
      ? `https://assessment.${stageDomain(stage)}`
      : serviceOrigin("assessment-app", stage),
    insightsApp: prod
      ? `https://insights.${stageDomain(stage)}`
      : serviceOrigin("insights-app", stage),
    feedbackApp: prod
      ? `https://feedback.${stageDomain(stage)}`
      : serviceOrigin("feedback-app", stage),
    newAssessmentApp: serviceOrigin("poc-assessment-app", stage),
    user: serviceOrigin("user", stage),
    userSvc: serviceOrigin("user-svc", stage),
    assessment: serviceOrigin("assessment", stage),
    tenderPackage: serviceOrigin("tender-package", stage),
    sender: `hello@${stageDomain(stage)}`,
    feedback: serviceOrigin("feedback", stage),
  };
};
